import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { collection, getDocs, query, where } from "firebase/firestore";
import { paths, REFERRAL_STATUS_LABEL, type Referral } from "@clevis/shared-types";
import { Card, CardTitle } from "@clevis/shared-ui";
import { db } from "../lib/firebase";

// Reports → Referrals (Clevis Relay): for the period, how often customers'
// links were opened, how many friends were sent, how many booked, the revenue
// their first invoices brought, what went out in rewards and what came back
// per dollar, and who sends the most.

const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : "–");
const dayKey = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export function ReferralsCard({ tenantId, start, end, rangeLabel }: { tenantId: string; start: number; end: number; rangeLabel: string }) {
  const [rows, setRows] = useState<Referral[]>([]);
  const [opens, setOpens] = useState(0);
  useEffect(() => {
    let dead = false;
    getDocs(query(collection(db, paths.tenantReferrals(tenantId)), where("createdAt", ">=", start), where("createdAt", "<", end)))
      .then((snap) => !dead && setRows(snap.docs.map((d) => d.data() as Referral)))
      .catch(() => !dead && setRows([]));
    getDocs(query(collection(db, paths.tenantReferralDays(tenantId)), where("day", ">=", dayKey(start)), where("day", "<", dayKey(end))))
      .then((snap) => !dead && setOpens(snap.docs.reduce((s, d) => s + ((d.get("opens") as number) ?? 0), 0)))
      .catch(() => !dead && setOpens(0));
    return () => {
      dead = true;
    };
  }, [tenantId, start, end]);

  const stats = useMemo(() => {
    const by = (s: Referral["status"]) => rows.filter((r) => r.status === s).length;
    const won = rows.filter((r) => r.status === "done" || r.status === "reward_due" || r.status === "paid");
    const revenue = won.reduce((s, r) => s + r.invoiceTotalCents, 0);
    const paid = rows.filter((r) => r.status === "paid");
    const rewards = paid.reduce((s, r) => s + r.rewardCents, 0);
    const top = new Map<string, { name: string; sent: number; won: number }>();
    for (const r of rows) {
      const t = top.get(r.referrerCustomerId) ?? { name: r.referrerName || "Customer", sent: 0, won: 0 };
      t.sent += 1;
      if (r.status === "done" || r.status === "reward_due" || r.status === "paid") t.won += 1;
      top.set(r.referrerCustomerId, t);
    }
    return {
      sent: rows.length,
      booked: by("booked") + by("done") + by("reward_due") + by("paid"),
      won: won.length,
      due: by("reward_due"),
      revenue,
      avgTicket: won.length ? Math.round(revenue / won.length) : 0,
      rewards,
      rewardsSent: paid.length,
      perDollar: rewards ? Math.round((revenue / rewards) * 100) : null,
      top: [...top.values()].sort((a, b) => b.won - a.won || b.sent - a.sent).slice(0, 5),
    };
  }, [rows]);

  return (
    <Card>
      <div className="mb-1 flex items-center justify-between gap-2">
        <CardTitle className="mb-0">Referrals · {rangeLabel}</CardTitle>
        <Link href="/referrals" className="text-xs text-steel hover:text-ink">
          Open Referrals
        </Link>
      </div>
      <p className="mb-2 text-xs text-steel">Clevis Relay: friends sent by customers' links in this period, and what came of them.</p>
      {rows.length === 0 && opens === 0 ? (
        <p className="text-xs text-steel">No referrals in this period.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr]">
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <span className="text-steel">Link opens</span>
            <span className="text-right font-semibold text-ink">{opens}</span>
            <span className="text-steel">Friends sent</span>
            <span className="text-right font-semibold text-ink">
              {stats.sent}
              {opens ? <span className="ml-1 font-normal text-steel">{pct(stats.sent, opens)} of opens</span> : null}
            </span>
            <span className="text-steel">Booked</span>
            <span className="text-right font-semibold text-ink">
              {stats.booked}
              {stats.sent ? <span className="ml-1 font-normal text-steel">{pct(stats.booked, stats.sent)}</span> : null}
            </span>
            <span className="text-steel">Jobs done &amp; paid</span>
            <span className="text-right font-semibold text-ink">
              {stats.won}
              {stats.sent ? <span className="ml-1 font-normal text-steel">{pct(stats.won, stats.sent)}</span> : null}
            </span>
            <span className="text-steel">Revenue from them</span>
            <span className="text-right font-semibold text-ink">{money(stats.revenue)}</span>
            <span className="text-steel">Average first invoice</span>
            <span className="text-right text-ink">{stats.won ? money(stats.avgTicket) : "–"}</span>
            <span className="text-steel">Rewards paid</span>
            <span className="text-right font-semibold text-ink">
              {money(stats.rewards)}
              {stats.rewardsSent ? <span className="ml-1 font-normal text-steel">· {stats.rewardsSent}</span> : null}
            </span>
            <span className="text-steel">Revenue per $1 in rewards</span>
            <span className="text-right text-ink">{stats.perDollar !== null ? money(stats.perDollar) : "–"}</span>
            {stats.due ? (
              <>
                <span className="text-steel">Rewards waiting</span>
                <span className="text-right font-semibold text-brandorange-dark">{stats.due}</span>
              </>
            ) : null}
          </div>
          <div className="text-xs">
            <div className="mb-1 font-semibold text-ink">Top referrers</div>
            {stats.top.length === 0 ? <p className="text-steel">Nobody yet.</p> : null}
            {stats.top.map((t) => (
              <div key={t.name} className="flex items-center justify-between py-0.5">
                <span className="truncate text-ink">{t.name}</span>
                <span className="shrink-0 text-steel">
                  {t.won} of {t.sent} {REFERRAL_STATUS_LABEL.done.toLowerCase()}
                </span>
              </div>
            ))}
            <p className="mt-2 text-[11px] text-steel">Referred customers also show as the "Referral" row under Lead sources above, and each reward sits on the friend's job as an expense.</p>
          </div>
        </div>
      )}
    </Card>
  );
}
